const SIGNUP_ATTRIBUTE_ALIASES = {
  given_name: ['given_name', 'first_name', 'firstName', 'givenName'],
  family_name: ['family_name', 'last_name', 'lastName', 'familyName'],
  'custom:acknowledgement': [
    'custom:acknowledgement',
    'acknowledgement',
    'copyrightAcknowledgement',
  ],
  'custom:copyright_statement': [
    'custom:copyright_statement',
    'copyright_statement',
    'copyrightStatement',
  ],
};

function getObjectValue(obj, key) {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }
  return Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
}

function pickAttributeValue(source, keys) {
  for (let i = 0; i < keys.length; i++) {
    const value = getObjectValue(source, keys[i]);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== '') {
        return trimmed;
      }
    }
  }
  return undefined;
}

function pickRawAttributeValue(source, keys) {
  for (let i = 0; i < keys.length; i++) {
    const value = getObjectValue(source, keys[i]);
    if (typeof value !== 'undefined' && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
}

function buildDisplayName(givenName, familyName) {
  return (
    [givenName, familyName]
      .filter(Boolean)
      .join(' ')
      .trim() || undefined
  );
}

export function normalizeCognitoAttributes(attributes = false) {
  if (Array.isArray(attributes)) {
    return attributes.reduce((normalized, attribute) => {
      if (
        attribute &&
        typeof attribute.Name === 'string' &&
        attribute.Name !== '' &&
        typeof attribute.Value !== 'undefined'
      ) {
        normalized[attribute.Name] = attribute.Value;
      }
      return normalized;
    }, {});
  }

  if (attributes && typeof attributes === 'object') {
    return { ...attributes };
  }

  return {};
}

export function extractSignupAttributes(metadata = false, attributes = false) {
  const metadataAttributes =
    metadata && metadata.attributes && typeof metadata.attributes === 'object'
      ? metadata.attributes
      : false;

  const sources = [attributes, metadataAttributes, metadata].filter(
    source => source && typeof source === 'object'
  );

  const signupAttributes = {};
  Object.keys(SIGNUP_ATTRIBUTE_ALIASES).forEach(attributeName => {
    const keys = SIGNUP_ATTRIBUTE_ALIASES[attributeName];
    for (let i = 0; i < sources.length; i++) {
      const value =
        attributeName === 'custom:copyright_statement'
          ? pickRawAttributeValue(sources[i], keys)
          : pickAttributeValue(sources[i], keys);
      if (typeof value !== 'undefined') {
        signupAttributes[attributeName] = value;
        break;
      }
    }
  });

  return signupAttributes;
}

export function buildCognitoSignupAttributes({
  username,
  tenant = false,
  metadata = false,
  attributes = false,
} = {}) {
  const signupAttributes = extractSignupAttributes(metadata, attributes);
  return {
    email: username,
    'custom:tenant': tenant,
    ...signupAttributes,
  };
}

export function mapCognitoUserRecord(user = {}) {
  const attributes = normalizeCognitoAttributes(
    user.Attributes || user.attributes
  );
  const givenName = pickAttributeValue(
    attributes,
    SIGNUP_ATTRIBUTE_ALIASES.given_name
  );
  const familyName = pickAttributeValue(
    attributes,
    SIGNUP_ATTRIBUTE_ALIASES.family_name
  );
  const copyrightAcknowledgement = pickRawAttributeValue(
    attributes,
    SIGNUP_ATTRIBUTE_ALIASES['custom:acknowledgement']
  );
  const copyrightStatement = pickRawAttributeValue(
    attributes,
    SIGNUP_ATTRIBUTE_ALIASES['custom:copyright_statement']
  );

  return {
    ...user,
    id: user.id || user.Username || user.username || false,
    username: user.username || user.Username || user.id || false,
    email: attributes.email || user.email || '',
    given_name: givenName || '',
    family_name: familyName || '',
    first_name: givenName || '',
    last_name: familyName || '',
    name: buildDisplayName(givenName, familyName) || '',
    tenant: attributes['custom:tenant'] || '',
    acknowledgement: copyrightAcknowledgement || '',
    copyright_statement: copyrightStatement || '',
    attributes,
  };
}

export function normalizeAuthenticatedCognitoUser(user = false) {
  if (!user || typeof user !== 'object') {
    return user;
  }

  const attributes = normalizeCognitoAttributes(user.attributes);
  const profile = mapCognitoUserRecord({
    id: user.username || user.id || false,
    username: user.username || user.id || false,
    email: attributes.email,
    attributes,
  });

  return {
    ...user,
    attributes,
    cognitoUser: user,
    profile,
  };
}
